import { AxiosError } from 'axios'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { event as gaEvent } from 'react-ga'
import { Trans, useTranslation } from 'react-i18next'
import { useMatch } from 'react-router'
import { ErrorsContext } from '../contexts/errors_context'
import { SettingsContext } from '../contexts/settings_context'
import { itemsRepository } from '../repositories/items_repository'

import '../stylesheets/Onboarding.scss'

import { Item } from '../types'
import { GenericNotFound } from './generic_not_found'

const ItemOnboard = (): JSX.Element => {
  const { i18n, t } = useTranslation()

  const { addError } = useContext(ErrorsContext)

  const { setMinimalUi } = useContext(SettingsContext)
  useEffect(() => {
    if (!setMinimalUi) return

    setMinimalUi(true)

    return () => {
      setMinimalUi(false)
    }
  }, [setMinimalUi])

  const [loading, setLoading] = useState<boolean>(true)
  const [item, setItem] = useState<Item>()
  const [stage, setStage] = useState<number>(0)

  const params = useMatch('/categories/:category_id/items/:item_id/ob')?.params

  const itemId = useMemo<number|undefined>(() => {
    const id = parseInt(params?.item_id ?? '')
    return isNaN(id) ? undefined : id
  }, [params])

  const getItem = useCallback((id: number) => {
    setLoading(true)

    itemsRepository
      .get(id)
      .then(({ item }) => {
        setItem(item)

        gaEvent({
          category: 'Onboarding',
          action: 'get-item',
          value: item.id,
        })
      })
      .catch((err: AxiosError) => {
        addError?.(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [addError])

  const image = useMemo(() => {
    if (!item || item.images.length === 0) return

    return item?.images[0]
  }, [item])

  useEffect(() => {
    if (itemId) {
      getItem(itemId)
    } else {
      setLoading(false)
    }
  }, [getItem, itemId])

  const advanceStage = useCallback((s: number) => {
    setStage(s)

    gaEvent({
      category: 'Onboarding',
      action: 'set-stage',
      label: `stage-${String(s + 1)}`,
      value: item?.id,
    })
  }, [item?.id])

  const itemName = useMemo(() => {
    if (!item) return ''

    return ((i18n.language === 'ja' && item.nameJa) ?? !item.nameEn ? item.nameJa : item.nameEn) ?? ''
  }, [i18n.language, item])

  const stage0 = useMemo(() => {
    return <>
      <div className='content'>
        <div className={ 'fanfare-text' }>
          <div>{ t('cards.onboard.got_it_1') }</div>
          <div>{ t('cards.onboard.got_it_2') }</div>
        </div>
        <div className='success-text'>
          <p>
            <Trans
              i18nKey="cards.onboard.added"
              values={ { name: itemName } }
              components={ { bold: <strong /> } }
            />
          </p>
        </div>
        {
          image
            ? <img className="card" src={ image.thumb2x } />
            : null
        }
        <p className='title'>{ itemName }</p>
      </div>
      <div className='buttons first'>
        <p>
          <button className='full' onClick={ () => { advanceStage(1) } }>{ t('cards.onboard.collect') }</button>
        </p>
        <p>
          <Trans
            i18nKey="cards.onboard.add_to_deck"
            values={ { name: itemName } }
            components={ { bold: <strong /> } }
          />
        </p>
      </div>
      <div className='buttons second'>
        <p>
          <button className='full' onClick={ () => { advanceStage(2) } }>{ t('cards.onboard.next') }</button>
        </p>
        <p>
          &nbsp;
        </p>
      </div>
    </>
  }, [advanceStage, image, itemName, t])

  const registerButtonClick = useCallback(() => {
    gaEvent({
      category: 'Onboarding',
      action: 'register-button-click',
      value: item?.id,
    })
  }, [item?.id])

  const stage2 = useMemo(() => {
    return <>
      <div className='content'>
        <div className='inner'>
          <h2>{ t('cards.onboard.welcome') }</h2>
          <p>
            { t('cards.onboard.intro') }
          </p>
          <p>
            { t('cards.onboard.bullet_deck') }
          </p>
          <p>
            { t('cards.onboard.bullet_share') }
          </p>
          <p>
            { t('cards.onboard.bullet_buy') }
          </p>
          <p>
            { t('cards.onboard.bullet_sell') }
          </p>
        </div>
      </div>
      <div className='buttons'>
        <p>
          <a className='button full' href={ `/register/nologin?item_id=${item?.id ?? ''}` } onClick={ registerButtonClick }>
            { t('cards.onboard.start') }
          </a>
        </p>
        <p>
          &nbsp;
        </p>
      </div>
    </>
  }, [item?.id, registerButtonClick, t])

  const stageUi = useMemo(() => {
    if (stage <= 1) return stage0

    return stage2
  }, [stage, stage0, stage2])

  if (loading) {
    return <p>{ t('cards.marketplace.loading') }</p>
  }

  if (!item) return <GenericNotFound />

  return (
    <div className={ `onboarding-container stage-${stage}` }>
      <div>
        <div className='header'>
          <h1 className='logo'>HOLIC</h1>
        </div>
        { stageUi }
      </div>
    </div>
  )
}

export { ItemOnboard }
